import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import PanelCard from '../../performance-operations/components/PanelCard';
import Button from '../../../components/ui/Button';
import ReminderDrawer from '../../performance-reminders/ReminderDrawer';
import { UnfilteredNote, useInsightDrawers } from '../../performance-operations/components/InsightPanels';
import { insightBody, insightTitle } from '../../performance-insights/config';
import { LIVE_INSIGHT_STATUSES, fetchInsights, usePanel } from '../../performance-operations/config';
import { formatDate } from '../../../utils/Utils';
import { insightTaskTarget, processHealthHref } from '../../../components/performance/processHealthLinks';
import {
  AiActions, AiBody, AiCard, AiGrid, AiMeta, AiTitle,
} from '../styles';
import { AI_CARDS, PROCESS_RULES } from '../config';

// Spec §7.2 — AI alanı: gecikme yoğunlaşması, aşama yavaşlaması ve yük birikimi kurallarının canlı bulguları (en çok 3). Kart
// işlemleri: Hatırlatma Hazırla (seçimi sunucu bulgudan kurar, §5.3/2), Aksiyon Aç, Görevleri Gör (bulgunun kapsamı/türüyle, §19/10),
// Detay. Bulgu ucu yalnız dönem süzgeci kabul eder → kapsam süzgeci etkinken "tüm şirket" notu.
export default function ProcessInsightsPanel({ filters, refreshKey, onChanged }) {
  const { t, i18n } = useTranslation();
  const periodId = filters.periodId;
  const [local, setLocal] = useState(0);
  const [reminder, setReminder] = useState(null);
  const panel = usePanel(() => fetchInsights({
    'periodId[eq]': periodId, 'ruleCode[in]': PROCESS_RULES.join(','), 'status[in]': LIVE_INSIGHT_STATUSES, pageNumber: 1, pageSize: AI_CARDS,
  }), [periodId, refreshKey, local], !!periodId);
  const changed = useCallback(() => { setLocal((v) => v + 1); onChanged?.(); }, [onChanged]);
  const { setDetail, setActionFor, drawers } = useInsightDrawers(changed);
  const rows = panel.data?.rows || [];

  return (
    <PanelCard
      panelKey="processInsights" title={t('performance.processHealth.panel.insights')} span={12}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={rows.length === 0}
      emptyText={t('performance.processHealth.panel.noInsights')}
      actions={<Link to="/performance/insights" data-link="all">{t('performance.operations.panel.seeAllPlain')}</Link>}
    >
      <UnfilteredNote filters={filters} />
      <AiGrid>
        {rows.map((r) => (
          <AiCard key={r.id} data-insight={r.id}>
            <AiTitle>{insightTitle(r, t)}</AiTitle>
            <AiBody>{insightBody(r, t)}</AiBody>
            <AiMeta>{t('performance.operations.panel.insightMeta', { n: r.n, date: formatDate(r.dataAsOf, i18n.language) })}</AiMeta>
            <AiActions>
              <Button variant="ghost" data-action="remind" onClick={() => setReminder({ periodId, sourceInsightId: r.id })}>
                {t('performance.processHealth.rowAction.prepareReminder')}
              </Button>
              <Button variant="ghost" data-action="open-action" onClick={() => setActionFor(r)}>
                {t('performance.operations.rowAction.openAction')}
              </Button>
              <Link to={processHealthHref(filters, insightTaskTarget(r))} data-link="tasks">{t('performance.processHealth.rowAction.viewTasks')}</Link>
              <Button variant="ghost" data-action="detail" onClick={() => setDetail(r)}>{t('performance.operations.rowAction.detail')}</Button>
            </AiActions>
          </AiCard>
        ))}
      </AiGrid>
      {drawers}
      <ReminderDrawer
        open={!!reminder}
        selection={reminder}
        onClose={() => setReminder(null)}
        onSent={() => { setReminder(null); changed(); }}
      />
    </PanelCard>
  );
}
